import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Route, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import OrderCard from "@/components/OrderCard";
import { optimizeRoute, isReordered } from "@/lib/micro_routing";

export default function DriverActive() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverPos, setDriverPos] = useState(null);
  const [optimized, setOptimized] = useState(false);

  useEffect(() => {
    api.get("/orders/mine").then(({ data }) => setOrders(data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setDriverPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 8000 },
    );
  }, []);

  const active = orders.filter((o) => !["delivered", "cancelled"].includes(o.status));
  const past = orders.filter((o) => ["delivered", "cancelled"].includes(o.status));

  // Phase 33 — Micro-routing suggestion (computed locally, applied only on user opt-in)
  const suggestedActive = useMemo(() => optimizeRoute(active, driverPos), [active, driverPos]);
  const canOptimize = !!driverPos && active.length >= 2 && isReordered(active, suggestedActive);
  const displayActive = optimized ? suggestedActive : active;

  return (
    <div className="space-y-6" data-testid="driver-active-page">
      <div>
        <p className="overline">In corso</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">Le mie consegne</h1>
      </div>

      <section>
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-display text-lg font-bold tracking-tight">Attive ({active.length})</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {active.length > 1 && (
              <span className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1" data-testid="multi-order-badge">
                Multi-ordine · gestisci una alla volta
              </span>
            )}
            {/* Phase 33 — Micro-routing CTA */}
            {canOptimize && (
              optimized ? (
                <button
                  onClick={() => setOptimized(false)}
                  data-testid="reset-route-btn"
                  className="text-[11px] inline-flex items-center gap-1 rounded-full bg-zinc-100 text-dapnow-ink px-2.5 py-1 font-semibold hover:bg-zinc-200"
                >
                  <Route className="h-3 w-3" /> Ripristina ordine originale
                </button>
              ) : (
                <button
                  onClick={() => { setOptimized(true); toast.success("Percorso riordinato in base alla tua posizione."); }}
                  data-testid="optimize-route-btn"
                  className="text-[11px] inline-flex items-center gap-1 rounded-full bg-dapnow-blue text-white px-2.5 py-1 font-semibold hover:opacity-90 shadow-sm"
                >
                  <Wand2 className="h-3 w-3" /> Ottimizza percorso
                </button>
              )
            )}
          </div>
        </div>
        {optimized && (
          <p className="text-xs text-emerald-700 -mt-2 mb-3" data-testid="optimize-active-note">
            🪄 Sequenza riordinata: il prossimo stop più vicino è in cima. Stima basata sulla tua posizione attuale e sullo stato di ogni ordine.
          </p>
        )}
        {loading ? <div className="card-soft p-8 text-center text-dapnow-muted">Caricamento…</div> :
          active.length === 0 ? <div className="card-soft p-8 text-center text-dapnow-muted">Nessuna consegna attiva.</div> :
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              {displayActive.map((o, idx) => (
                <div key={o.id} className="relative min-w-0">
                  <span className="absolute -top-2 -left-2 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-dapnow-blue text-white text-xs font-bold shadow" data-testid={`active-order-step-${o.id}`}>
                    {idx + 1}
                  </span>
                  {optimized && typeof o._suggested_km === "number" && (
                    <span className="absolute -top-2 right-2 z-10 inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 shadow" data-testid={`suggested-leg-${o.id}`}>
                      {o._suggested_next === "delivery" ? "🚚 consegna" : "📦 ritiro"} · +{o._suggested_km} km
                    </span>
                  )}
                  <OrderCard order={o} to={`/driver/orders/${o.id}`} testId={`driver-active-card-${o.id}`} />
                </div>
              ))}
            </div>
            {active.length > 1 && (
              <p className="mt-3 text-xs text-dapnow-muted leading-relaxed" data-testid="multi-order-hint">
                💡 Per ora completa <strong>l'ordine #1</strong> (vai al ritiro → consegna), poi passa al successivo. Ogni ordine è separato: tracking, payout e prove di consegna restano indipendenti.
              </p>
            )}
          </>
        }
      </section>

      <section>
        <h2 className="font-display text-lg font-bold tracking-tight mb-3">Storico</h2>
        {past.length === 0 ? <div className="card-soft p-8 text-center text-dapnow-muted">Nessuna consegna conclusa.</div> :
          <div className="grid sm:grid-cols-2 gap-4">
            {past.map((o) => <OrderCard key={o.id} order={o} to={`/driver/orders/${o.id}`} testId={`driver-past-card-${o.id}`} />)}
          </div>
        }
      </section>
    </div>
  );
}
